import { detectLegacyFile, planLegacyArticles, planLegacyImport, planLegacyWorkOrderExtras, type ImportPlan, type LegacyFileKind, type Row } from "@szerviz/core";
import Papa from "papaparse";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorText } from "../components/ErrorText";
import { Help } from "../components/Help";
import { useApp } from "../context";
import type { ImportProgress, ImportResult, WorkOrderImportResult } from "../data";

async function readCsv(file: File): Promise<{ kind: LegacyFileKind; rows: Row[] }> {
  const text = await file.text();
  const parsed = Papa.parse<Row>(text, { header: true, skipEmptyLines: true });
  const kind = detectLegacyFile(parsed.meta.fields ?? [], file.name);
  return { kind, rows: parsed.data };
}

type Files = Partial<Record<LegacyFileKind, Row[]>>;

export function Import() {
  const { t } = useTranslation();
  const { store, workshop } = useApp();
  const [files, setFiles] = useState<Files>({});
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [woResult, setWoResult] = useState<WorkOrderImportResult | null>(null);
  const [extrasResult, setExtrasResult] = useState<number | null>(null);
  const [articlesResult, setArticlesResult] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const pick = async (list: FileList | null) => {
    setError(null);
    setResult(null);
    setWoResult(null);
    setExtrasResult(null);
    setArticlesResult(null);
    const next: Files = { ...files };
    try {
      for (const f of Array.from(list ?? [])) {
        const { kind, rows } = await readCsv(f);
        next[kind] = rows;
      }
    } catch (e) {
      setError(e);
    }
    setFiles(next);
    const c = next.customers ?? [];
    const v = next.vehicles ?? [];
    setPlan(c.length || v.length ? planLegacyImport(c, v) : null);
  };

  const workOrders = files.workOrders ?? [];
  const extras = files.workOrderExtras ? planLegacyWorkOrderExtras(files.workOrderExtras) : null;
  const articles = files.articles ? planLegacyArticles(files.articles) : null;
  const ready = !!plan || workOrders.length > 0 || !!extras || !!articles;
  const done = !!result || !!woResult || extrasResult !== null || articlesResult !== null;

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      if (plan) setResult(await store.importLegacy(workshop.id, plan, setProgress));
      if (workOrders.length) setWoResult(await store.importLegacyWorkOrders(workshop.id, workOrders, files.workOrderItems ?? [], setProgress));
      // Odometer and remarks complete work orders that are already in the program.
      if (extras) setExtrasResult(await store.supplementLegacyWorkOrders(workshop.id, extras.rows, setProgress));
      if (articles) setArticlesResult(await store.importLegacyArticles(workshop.id, articles.articles, setProgress));
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const phaseKey: Record<string, string> = {
    customers: "import.progressCustomers",
    vehicles: "import.progressVehicles",
    workOrders: "import.progressWorkOrders",
    extras: "import.progressExtras",
    articles: "import.progressArticles",
  };

  return (
    <section className="narrow">
      <h1>
        {t("import.title")} <Help id="import.page" />
      </h1>
      <p>{t("import.intro")}</p>
      <label className="field wide">
        <span>{t("import.files")}</span>
        <input type="file" accept=".csv,text/csv" multiple onChange={(e) => void pick(e.target.files)} aria-label={t("import.files")} />
      </label>
      <ErrorText error={error} />
      {plan && (
        <p className="info">
          {t("import.preview", {
            customers: String(plan.customers.length),
            vehicles: String(plan.vehicles.length),
            skipped: String(plan.skipped),
          })}
          {plan.merged > 0 && <> {t("import.merged", { count: String(plan.merged) })}</>}
        </p>
      )}
      {workOrders.length > 0 && (
        <p className="info">
          {t("import.previewWorkOrders", { workOrders: String(workOrders.length), items: String(files.workOrderItems?.length ?? 0) })}
        </p>
      )}
      {extras && (
        <p className="info">
          {t("import.previewExtras", { km: String(extras.withKm), notes: String(extras.withNotes) })}
          {extras.implausibleKm > 0 && <> {t("import.implausibleKm", { count: String(extras.implausibleKm) })}</>}
        </p>
      )}
      {articles && (
        <p className="info">
          {t("import.previewArticles", { count: String(articles.articles.length), freeText: String(articles.freeText) })}
        </p>
      )}
      {ready && !done && (
        <button type="button" className="primary" disabled={busy} onClick={() => void start()}>
          {t("import.start")}
        </button>
      )}
      {progress && progress.phase !== "done" && busy && (
        <p>
          {t(phaseKey[progress.phase] ?? "import.progressCustomers", {
            done: String(progress.done),
            total: String(progress.total),
          })}
        </p>
      )}
      {result && (
        <p className="ok">
          {t("import.done", {
            customers: String(result.customers),
            vehicles: String(result.vehicles),
            withoutOwner: String(result.vehiclesWithoutOwner),
          })}
        </p>
      )}
      {articlesResult !== null && <p className="ok">{t("import.doneArticles", { count: String(articlesResult) })}</p>}
      {extrasResult !== null && <p className="ok">{t("import.doneExtras", { count: String(extrasResult) })}</p>}
      {woResult && (
        <p className="ok">
          {t("import.doneWorkOrders", {
            workOrders: String(woResult.workOrders),
            items: String(woResult.items),
            skippedOrders: String(woResult.skippedOrders),
            skippedItems: String(woResult.skippedItems),
            toReview: String(woResult.toReview),
          })}
        </p>
      )}
    </section>
  );
}
